import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TimePicker } from "antd";
import moment from "moment";
import AppSwitch from "components/ui/AppSwitch.jsx";
import { ClockCircleOutlined } from "@ant-design/icons";

const OrderHourSetRow = (props) => {
  const { t } = useTranslation();
  const [isFromFocused, setIsFromFocused] = useState(false);
  const [isToFocused, setIsToFocused] = useState(false);

  const getValue = (key) => {
    if (!props.workingHours) return null;
    if (!props.workingHours[key]) return null;
    return moment(props.workingHours[key]);
  };

  const handleTimeChange = (key, value) => {
    props.handleValues(key, value ? value.valueOf() : null);
  };

  // Day toggle should not clear stored/default times.
  // Use explicit day flag when present, fallback to start-time presence.
  const isDayEnabled =
    typeof props.workingHours?.[props.keyEnabled] === "boolean"
      ? props.workingHours?.[props.keyEnabled]
      : props.workingHours?.[props.keyStartTime] != null;

  const handleSwitchChange = (checked) => {
    if (checked) {
      if (props.keyEnabled) {
        props.handleValues(props.keyEnabled, true);
      }
      // Enable day - set default times if not set
      if (!props.workingHours?.[props.keyStartTime]) {
        const defaultStart = moment().hour(9).minute(0).toDate();
        const defaultEnd = moment().hour(17).minute(0).toDate();
        props.handleValues(props.keyStartTime, defaultStart);
        props.handleValues(props.keyEndTime, defaultEnd);
      }
    } else {
      if (props.keyEnabled) {
        props.handleValues(props.keyEnabled, false);
      }
    }
  };

  // Capitalize first letter of day name
  const capitalizeDay = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <div className="w-full">
      <style>{`
        .app-time-picker-custom.ant-picker {
          width: 100%;
          border-radius: 0.5rem !important;
          border: 1px solid #d1d5db !important;
          padding: 0.5rem 0.75rem !important;
          min-height: 40px !important;
          background-color: white !important;
          transition: box-shadow 0.15s ease, border-color 0.15s ease;
        }

        .app-time-picker-custom.app-time-picker-focused.ant-picker {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
        }

        .app-time-picker-custom .ant-picker-input > input {
          color: #374151 !important;
          font-size: 0.875rem !important;
          line-height: 1.5rem !important;
          text-align: left !important;
        }

        .app-time-picker-custom .ant-picker-input > input::placeholder {
          color: #9ca3af !important;
        }

        .app-time-picker-custom.ant-picker-disabled {
          background-color: #f3f4f6 !important;
          border-color: #e5e7eb !important;
        }

        .app-time-picker-custom.ant-picker-disabled .ant-picker-input > input {
          color: #9ca3af !important;
        }

        .app-time-picker-custom .ant-picker-suffix {
          color: #6b7280 !important;
        }

        ${props.s1Error ? `
          .app-time-picker-custom.ant-picker {
            border-color: #ef4444 !important;
          }
        ` : ''}
      `}</style>

      
      <div className="mb-3 flex w-full flex-nowrap items-center gap-5">
      <div className="flex w-1/6 shrink-0">
          <div className="flex items-center gap-8 w-full">
            <span className="text-sm font-semibold text-gray-900 w-20 flex-shrink-0">
              {capitalizeDay(props.label)}
            </span>
            <AppSwitch 
              checked={isDayEnabled}
              onChange={handleSwitchChange}
              labelLeft={false}
            />
          </div>
          
          {/* Add Hours Button - below time fields in Day column */}
          
        </div>

        {/* From Column */}
        <div className="flex w-1/4 shrink-0">
          <TimePicker
            className={`app-time-picker-custom ${isFromFocused ? "app-time-picker-focused" : ""}`}
            value={getValue(props.keyStartTime)}
            onChange={(d) => handleTimeChange(props.keyStartTime, d)}
            format="hh:mm A"
            minuteStep={5}
            allowClear={false}
            inputReadOnly
            placeholder={t("Select time")}
            onOpenChange={(open) => setIsFromFocused(open)}
            suffixIcon={<ClockCircleOutlined className="text-gray-500" />}
            disabled={!isDayEnabled}
          />
        </div>

        {/* To Column */}
        <div className="flex w-1/4 shrink-0">
          <TimePicker
            className={`app-time-picker-custom ${isToFocused ? "app-time-picker-focused" : ""}`}
            value={getValue(props.keyEndTime)}
            onChange={(d) => handleTimeChange(props.keyEndTime, d)}
            format="hh:mm A"
            minuteStep={5}
            allowClear={false}
            inputReadOnly
            placeholder={t("Select time")}
            onOpenChange={(open) => setIsToFocused(open)}
            suffixIcon={<ClockCircleOutlined className="text-gray-500" />}
            disabled={!isDayEnabled}
          />
        </div>
        <div className="flex w-1/4 shrink-0">
        {!props.showSlot2 && (
            <div className="">
              <button
                type="button"
                onClick={props.addSlotInput}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                disabled={!isDayEnabled}
              >
                <span className="w-6 h-6 flex items-center justify-center border border-gray-400 rounded-full text-gray-600 hover:border-gray-600 hover:text-gray-800">
                  +
                </span>
                {t("Add Hours")}
              </button>
            </div>
          )}
        </div>

      </div>
    

      {/* SLOT 2 */}
      {props.showSlot2 && (
        <div className="flex flex-nowrap items-center justify-between py-3">
          <div className="w-1/6 shrink-0">
          </div>
          <div className="w-1/4 shrink-0">
            <TimePicker
              className={`app-time-picker-custom ${props.s2Error || props.overLapError ? "border-red-500" : ""}`}
              value={getValue(props.keyStartTime2)}
              onChange={(d) => handleTimeChange(props.keyStartTime2, d)}
              format="hh:mm A"
              minuteStep={5}
              allowClear={false}
              inputReadOnly
              placeholder={t("Select time")}
              suffixIcon={<ClockCircleOutlined className="text-gray-500" />}
            />
          </div>
          <div className="w-1/4 shrink-0">
            <TimePicker
              className={`app-time-picker-custom ${props.s2Error || props.overLapError ? "border-red-500" : ""}`}
              value={getValue(props.keyEndTime2)}
              onChange={(d) => handleTimeChange(props.keyEndTime2, d)}
              format="hh:mm A"
              minuteStep={5}
              allowClear={false}
              inputReadOnly
              placeholder={t("Select time")}
              suffixIcon={<ClockCircleOutlined className="text-gray-500" />}
            />
          </div>
          <div className="w-1/4 shrink-0">
            <button
              type="button"
              onClick={props.removeSlotInput}
              className="text-sm text-red-600 hover:text-red-800"
            >
              {t("Remove")}
            </button>
          </div>
        </div>
      )}

      {(props.s1Error || props.s2Error || props.overLapError) && (
        <p className="text-sm text-red-500 pl-8">
          {props.s1Error || props.s2Error || props.overLapError}
        </p>
      )}
    </div>
  );
};

export default OrderHourSetRow;
